import React, { useState, useEffect } from 'react';
import { Copy, RefreshCw, Plus, Trash2, Eye, EyeOff, Key, Server } from 'lucide-react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { environment } from '../../Env_data/environment';

const MCPOAuthSection = ({ userId }) => {
  const [clients, setClients] = useState([]);
  const [bearerToken, setBearerToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [visibleSecrets, setVisibleSecrets] = useState({});
  const [visibleBearer, setVisibleBearer] = useState(false);
  const [generatingToken, setGeneratingToken] = useState({});
  const { enqueueSnackbar } = useSnackbar();

  // Build API base URL from environment
  const API_BASE = environment.BASE_URL ? environment.BASE_URL.replace('/api/', '') : 'http://localhost/backend/api';
  
  // Construct MCP Server URL
  const getMcpServerUrl = () => {
    return `${API_BASE}/mcp/server.php`;
  };

  // Fetch OAuth clients and Bearer token info
  const fetchClients = async () => {
    try {
      setLoading(true);
      const url = `${API_BASE}/mcp/api/oauth_clients.php?user_id=${userId}`;
      const response = await axios.get(url);
      if (response.data.success) {
        setClients(response.data.clients);
      }
      
      // Fetch Bearer token info
      const bearerUrl = `${API_BASE}/mcp/api/bearer_token.php?user_id=${userId}`;
      const bearerResponse = await axios.get(bearerUrl);
      if (bearerResponse.data.success && bearerResponse.data.has_token) {
        setBearerToken({
          created_at: bearerResponse.data.token_created_at,
          expires_at: bearerResponse.data.token_expires_at,
          is_expired: bearerResponse.data.is_expired
        });
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch credentials', { variant: 'error' });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchClients();
    }
  }, [userId]);

  // Create new OAuth client
  const handleCreateClient = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_BASE}/mcp/api/oauth_clients.php?user_id=${userId}&action=create`,
        {
          client_name: 'Spent MCP Server',
          redirect_uri: 'http://localhost:3000/mcp/callback'
        }
      );
      
      if (response.data.success) {
        setClients([...clients, response.data.client]);
        setShowCreateForm(false);
        enqueueSnackbar('OAuth client created successfully', { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to create OAuth client', { variant: 'error' });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Regenerate client secret
  const handleRegenerateSecret = async (clientId) => {
    try {
      const response = await axios.post(
        `${API_BASE}/mcp/api/oauth_clients.php?user_id=${userId}&action=regenerate`,
        { client_id: clientId }
      );
      
      if (response.data.success) {
        setClients(
          clients.map(c => 
            c.client_id === clientId 
              ? { ...c, client_secret: response.data.client_secret }
              : c
          )
        );
        enqueueSnackbar('Client secret regenerated', { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to regenerate secret', { variant: 'error' });
      console.error(error);
    }
  };

  // Delete OAuth client
  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this OAuth client?')) {
      return;
    }
    
    try {
      const response = await axios.post(
        `${API_BASE}/mcp/api/oauth_clients.php?user_id=${userId}&action=delete`,
        { client_id: clientId }
      );
      
      if (response.data.success) {
        setClients(clients.filter(c => c.client_id !== clientId));
        enqueueSnackbar('OAuth client deleted', { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to delete client', { variant: 'error' });
      console.error(error);
    }
  };

  // Generate access token
  const handleGenerateToken = async (client) => {
    try {
      setGeneratingToken(prev => ({ ...prev, [client.client_id]: true }));
      const response = await axios.post(
        `${API_BASE}/mcp/api/oauth_token.php?action=generate`,
        {
          client_id: client.client_id,
          client_secret: client.client_secret
        }
      );
      
      if (response.data.success) {
        const token = response.data.access_token;
        // Copy to clipboard
        navigator.clipboard.writeText(token);
        enqueueSnackbar('Access token copied to clipboard', { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to generate token', { variant: 'error' });
      console.error(error);
    } finally {
      setGeneratingToken(prev => ({ ...prev, [client.client_id]: false }));
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar(`${label} copied to clipboard`, { variant: 'info' });
  };

  const toggleSecretVisibility = (clientId) => {
    setVisibleSecrets(prev => ({
      ...prev,
      [clientId]: !prev[clientId]
    }));
  };

  // Generate Bearer token
  const handleGenerateBearerToken = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_BASE}/mcp/api/bearer_token.php?user_id=${userId}&action=generate`
      );
      
      if (response.data.success) {
        // Copy token to clipboard
        navigator.clipboard.writeText(response.data.bearer_token);
        setBearerToken({
          created_at: new Date().toISOString(),
          expires_at: response.data.expires_at,
          is_expired: false,
          token: response.data.bearer_token // Store temporarily for display
        });
        enqueueSnackbar('Bearer token generated and copied to clipboard', { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to generate Bearer token', { variant: 'error' });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Revoke Bearer token
  const handleRevokeBearerToken = async () => {
    if (!window.confirm('Are you sure you want to revoke your Bearer token?')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_BASE}/mcp/api/bearer_token.php?user_id=${userId}&action=revoke`
      );
      
      if (response.data.success) {
        setBearerToken(null);
        enqueueSnackbar('Bearer token revoked', { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to revoke Bearer token', { variant: 'error' });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-purple-100 mb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Server className="w-8 h-8 text-purple-600" />
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
            MCP Server Integration
          </h2>
        </div>
        {!showCreateForm && (
          <button
            onClick={() => setShowCreateForm(true)}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Client
          </button>
        )}
      </div>

      {/* Info Text */}
      <p className="text-gray-600 mb-6 leading-relaxed">
        Create OAuth clients to connect this Spent App with Claude AI or other AI assistants via the Model Context Protocol (MCP). 
        Use the credentials below to authenticate your MCP server integration.
      </p>

      {/* Production-Ready Credentials Summary */}
      {clients.length > 0 && (
        <div className="mb-8 p-4 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-300 rounded-lg">
          <div className="flex items-start gap-2 mb-4">
            <span className="text-2xl">🚀</span>
            <div>
              <h3 className="font-semibold text-gray-900">Ready to Connect</h3>
              <p className="text-xs text-gray-600 mt-1">Copy these credentials and paste into your ChatGPT/Claude connector</p>
            </div>
          </div>

          <div className="space-y-3">
            {/* MCP Server Endpoint */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">🌐 MCP Server Endpoint</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white px-3 py-2 rounded border border-gray-300 text-xs font-mono text-gray-900 overflow-x-auto break-all">
                  {getMcpServerUrl()}
                </code>
                <button
                  onClick={() => copyToClipboard(getMcpServerUrl(), 'MCP Server URL')}
                  className="p-2 bg-purple-600 text-white hover:bg-purple-700 rounded transition-colors flex-shrink-0"
                  title="Copy MCP Server URL"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Client ID from First Client */}
            {clients.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">🔑 Client ID</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white px-3 py-2 rounded border border-gray-300 text-xs font-mono text-gray-900 overflow-x-auto break-all">
                    {clients[0].client_id}
                  </code>
                  <button
                    onClick={() => copyToClipboard(clients[0].client_id, 'Client ID')}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Client Secret from First Client */}
            {clients.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">🔐 Client Secret</label>
                <div className="flex items-center gap-2">
                  <input
                    type={visibleSecrets[clients[0].client_id] ? 'text' : 'password'}
                    value={clients[0].client_secret}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded text-xs font-mono text-gray-900"
                  />
                  <button
                    onClick={() => toggleSecretVisibility(clients[0].client_id)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                  >
                    {visibleSecrets[clients[0].client_id] ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => copyToClipboard(clients[0].client_secret, 'Client Secret')}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bearer Token Section */}
      <div className="mb-8 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg">
        <div className="flex items-start gap-2 mb-4">
          <span className="text-2xl">🔐</span>
          <div>
            <h3 className="font-semibold text-gray-900">Bearer Token (Long-lived)</h3>
            <p className="text-xs text-gray-600 mt-1">Alternative authentication method - valid for 1 year</p>
          </div>
        </div>

        {bearerToken && !bearerToken.is_expired ? (
          <div className="space-y-3">
            <div className="p-3 bg-white rounded-lg border border-green-200">
              <p className="text-xs font-medium text-gray-600 mb-2">📋 Your Bearer Token</p>
              <div className="flex items-center gap-2">
                <input
                  type={visibleBearer ? 'text' : 'password'}
                  value={bearerToken.token || '••••••••••••••••••••'}
                  readOnly
                  className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded text-xs font-mono text-gray-900"
                />
                <button
                  onClick={() => setVisibleBearer(!visibleBearer)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                >
                  {visibleBearer ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                {bearerToken.token && (
                  <button
                    onClick={() => copyToClipboard(bearerToken.token, 'Bearer Token')}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Created: {new Date(bearerToken.created_at).toLocaleDateString()} | 
                Expires: {new Date(bearerToken.expires_at).toLocaleDateString()}
              </p>
            </div>
            <button
              onClick={handleRevokeBearerToken}
              disabled={loading}
              className="w-full px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors text-sm font-medium"
            >
              Revoke Bearer Token
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="p-3 bg-white rounded-lg border border-green-200">
              <p className="text-sm text-gray-700 mb-3">
                No Bearer token found. Generate one to use long-lived authentication with the MCP server.
              </p>
              <button
                onClick={handleGenerateBearerToken}
                disabled={loading}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors font-medium flex items-center justify-center gap-2"
              >
                <Key className="w-4 h-4" />
                Generate Bearer Token
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Create New OAuth Client</h3>
            <button
              onClick={() => setShowCreateForm(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            A new OAuth client will be created for your Spent MCP Server integration.
          </p>
          <div className="flex gap-3">
            <button
              onClick={handleCreateClient}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors font-medium"
            >
              {loading ? 'Creating...' : 'Create Client'}
            </button>
          </div>
        </div>
      )}

      {/* Clients List */}
      {clients.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Key className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-4">No OAuth clients created yet</p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create First Client
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {clients.map((client) => (
            <div
              key={client.client_id}
              className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors"
            >
              {/* Client Name and Created Date */}
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">{client.client_name}</h3>
                <span className="text-xs text-gray-500">
                  {new Date(client.created_at).toLocaleDateString()}
                </span>
              </div>

              {/* Client ID */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Client ID
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={client.client_id}
                    readOnly
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm font-mono text-gray-600"
                  />
                  <button
                    onClick={() => copyToClipboard(client.client_id, 'Client ID')}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Copy Client ID"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Client Secret */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Client Secret
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type={visibleSecrets[client.client_id] ? 'text' : 'password'}
                    value={client.client_secret}
                    readOnly
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm font-mono text-gray-600"
                  />
                  <button
                    onClick={() => toggleSecretVisibility(client.client_id)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Toggle visibility"
                  >
                    {visibleSecrets[client.client_id] ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => copyToClipboard(client.client_secret, 'Client Secret')}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Copy Client Secret"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleGenerateToken(client)}
                  disabled={generatingToken[client.client_id]}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50 transition-colors text-sm font-medium"
                  title="Generate access token"
                >
                  <Key className="w-4 h-4" />
                  {generatingToken[client.client_id] ? 'Generating...' : 'Get Access Token'}
                </button>

                <button
                  onClick={() => handleRegenerateSecret(client.client_id)}
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded hover:bg-amber-100 transition-colors text-sm font-medium"
                  title="Regenerate secret"
                >
                  <RefreshCw className="w-4 h-4" />
                  Regenerate
                </button>

                <button
                  onClick={() => handleDeleteClient(client.client_id)}
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded hover:bg-red-100 transition-colors"
                  title="Delete client"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>


            </div>
          ))}
        </div>
      )}

      {/* Instructions */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-3">How to use with Claude:</h3>
        <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
          <li>Create an OAuth client above</li>
          <li>Copy your Client ID and Client Secret</li>
          <li>Generate an Access Token and use it in your MCP client</li>
          <li>Configure Claude Desktop to use the Spent MCP Server</li>
          <li>Ask Claude about your homes and expenses using natural language</li>
        </ol>
      </div>
    </div>
  );
};

export default MCPOAuthSection;
